import React, { useMemo, useState } from "react";

import PanelTable from "ui/PanelTable";
import Panel from "ui/Panel";
import Grid, { GridColumn, NumberCell } from "ui/Grid";
import CopyToClipboard from "ui/CopyToClipboard";
import SQL from "ui/SQL";

import { formatNumber, formatPercent } from "utils/format";
import { IndexType, IndexSelectionResultType } from "../util";

import ScanReference from "../ScanReference";
import IndexReference from "../IndexReference";
import { makeFilter } from "utils/filter";
import { useQueryStringSearch } from "ui/QueryStringSearch";
import Tip from "ui/Tip";
import { useRoutes } from "utils/routes";
import { Link } from "react-router-dom";

const IndexDetails: React.FunctionComponent<{
  result: IndexSelectionResultType;
  databaseId: string;
  index: IndexType;
}> = ({ result, index, databaseId }) => {
  return (
    <div>
      <IndexInfoPanel index={index} databaseId={databaseId} />
      <IndexScansPanel result={result} index={index} />
      <IndexQueriesPanel result={result} index={index} />
    </div>
  );
};

const IndexInfoPanel: React.FunctionComponent<{
  index: IndexType;
  databaseId: string;
}> = ({ index, databaseId }) => {
  const { databaseIndex } = useRoutes();

  const ddl = index.ddl + ";";
  const status =
    index.existing && index.selected
      ? "Existing index to keep"
      : index.existing
      ? "Existing index to drop"
      : index.selected
      ? "New index to add"
      : "Other considered index";

  const name = index.existing ? (
    <Link to={databaseIndex(databaseId, index.pganalyzeId)}>{index.name}</Link>
  ) : (
    <>&mdash;</>
  );

  return (
    <Panel title="Index Details">
      <PanelTable horizontal borders>
        <tbody>
          <tr>
            <th>Name</th>
            <td className="font-mono">{name}</td>
            <th>Structure</th>
            <td className="font-mono">{index.structure}</td>
          </tr>
          <tr>
            <th>Status</th>
            <td>{status}</td>
            <th>Index Write Overhead</th>
            <td>{formatNumber(index.writeOverhead, 2)}</td>
          </tr>
        </tbody>
      </PanelTable>
      <div className="p-2 bg-[#f5f5f8]">
        <CopyToClipboard
          className="block mt-1 float-right"
          label="Copy command"
          content={ddl}
        />
        <SQL inline className="text-base" sql={ddl} />
      </div>
    </Panel>
  );
};

const IndexScansPanel: React.FunctionComponent<{
  result: IndexSelectionResultType;
  index: IndexType;
}> = ({ result, index }) => {
  const [searchTerm, SearchInput] = useQueryStringSearch("qs");
  const scanData = useMemo(() => {
    return index.scans.map((scanCost) => {
      const scan = result.scans[scanCost.scanId];
      const estCurrCost = scan.bestExistingCost;
      const estNewCost = scan.bestSelectedCost;
      const indexChanged = estCurrCost.indexId !== estNewCost.indexId;
      return {
        ...scan,
        cost: scanCost.cost,
        combinedClauses: scan.combinedExpression,
        indexChanged,
        estCurrCost: estCurrCost.cost,
        currIndex: estCurrCost.indexId,
        estNewCost: estNewCost.cost,
        newIndex: estNewCost.indexId,
      };
    });
  }, [index.scans, result]);
  // If there are no scans that are expected to use this index, show all scans
  // by default. There's little point in forcing users to click "show all" in
  // this situation.
  const thisIndexScanCount = scanData.filter(
    (scan) => scan.newIndex === index.id,
  ).length;
  const [showAll, setShowAll] = useState(thisIndexScanCount === 0);

  const filteredScanData = scanData
    .filter(
      makeFilter(
        searchTerm,
        "combinedClauses",
        "label",
        "currIndex",
        "newIndex",
      ),
    )
    .filter((scan) => {
      return showAll || scan.newIndex === index.id;
    });
  const noRowsText =
    scanData.length === 0
      ? `No scans found to ${showAll ? "be using" : "prefer"} this index`
      : "No matching scans found";

  const columns: GridColumn<
    (typeof filteredScanData)[number],
    keyof (typeof filteredScanData)[number]
  >[] = [
    {
      field: "combinedClauses",
      header: "Scan Expression",
      width: "1fr",
      renderer: function ScanDetailsCell({ rowData }) {
        return <ScanReference className="w-full" scan={rowData} />;
      },
    },
    {
      field: "scansPerMin",
      header: "Est. Scans/min",
      width: "10%",
      style: "number",
      renderer: NumberCell.precision(2),
    },
    {
      field: "cost",
      header: "This Index Cost",
      width: showAll ? "10%" : false,
      style: "number",
      renderer: NumberCell,
    },
    {
      field: "estCurrCost",
      header: "Est. Cost",
      width: "10%",
      style: "number",
      nullValue: "n/a",
      renderer: NumberCell,
    },
    {
      field: "currIndex",
      header: "",
      width: "40px",
      disableSort: true,
      title: ({ fieldData }) => {
        if (!fieldData) {
          return "Sequential Scan";
        }

        return result.indexes[fieldData].structure;
      },
      renderer: function IndexCell({ fieldData }) {
        return (
          <IndexReference
            terse
            className="w-full"
            index={result.indexes[fieldData]}
          />
        );
      },
    },
    {
      field: "estNewCost",
      header: "Est. New Cost",
      width: "10%",
      style: "number",
      nullValue: "n/a",
      renderer: function EstNewCostCell({ rowData, fieldData }) {
        if (!rowData.indexChanged) {
          return "no change";
        }
        return formatNumber(fieldData);
      },
    },
    {
      field: "newIndex",
      header: "",
      width: "40px",
      disableSort: true,
      title: ({ rowData, fieldData }) => {
        if (!fieldData) {
          return "Sequential Scan";
        }
        if (!rowData.indexChanged) {
          return undefined;
        }

        return result.indexes[fieldData].structure;
      },
      renderer: function IndexCell({ rowData, fieldData }) {
        if (!rowData.indexChanged) {
          return null;
        }
        return (
          <IndexReference
            terse
            className="w-full"
            index={result.indexes[fieldData]}
          />
        );
      },
    },
  ];

  return (
    <Panel
      title="Scans"
      secondaryTitle={
        <>
          <div>
            <label>
              <input
                type="checkbox"
                className="!mr-1"
                checked={showAll}
                onChange={(evt) => setShowAll(evt.target.checked)}
              />
              Show all scans{" "}
              <Tip
                content={
                  <>
                    If checked, shows all scans that could potentially use this
                    index, even if this index is not the best option in the
                    current index selection recommendation.
                    <br />
                    If unchecked, only shows scans that are expected to use this
                    index (i.e., for which this index offers the lowest cost).
                  </>
                }
              />
            </label>
          </div>
          <SearchInput />
        </>
      }
    >
      <Grid
        noRowsText={noRowsText}
        striped
        defaultSortBy="cost"
        sortParam="ss"
        data={filteredScanData}
        columns={columns}
      />
    </Panel>
  );
};

const IndexQueriesPanel: React.FunctionComponent<{
  result: IndexSelectionResultType;
  index: IndexType;
}> = ({ result, index }) => {
  const { isPrimaryInCluster } = result;
  const { databaseQuery } = useRoutes();
  const [searchTerm, SearchInput] = useQueryStringSearch("qq");
  const queryData = useMemo(() => {
    return index.scans.flatMap((scanCost) => {
      const scan = result.scans[scanCost.scanId];
      const estCurrCost = scan.bestExistingCost;
      const estNewCost = scan.bestSelectedCost;
      const indexChanged = estCurrCost.indexId !== estNewCost.indexId;
      return scan.queries.map((query) => {
        return {
          scan,
          scanLabel: scan.label,
          queryId: query.id,
          serverName: query.serverName,
          databaseId: query.databaseId,
          normalizedQuery: query.normalizedQuery,
          truncatedQuery: query.truncatedQuery,
          callsPerMinute: query.callsPerMinute,
          pctOfTotal: query.pctOfTotal,
          avgTime: query.avgTime,
          cost: scanCost.cost,
          indexChanged,
          estCurrCost: estCurrCost.cost,
          estNewCost: estNewCost.cost,
          newIndex: estNewCost.indexId,
        };
      });
    });
  }, [index.scans, result]);
  // If there are no scans that are expected to use this index, show all scans
  // by default. There's little point in forcing users to click "show all" in
  // this situation.
  const thisIndexScanCount = queryData.filter(
    (query) => query.newIndex === index.id,
  ).length;
  const [showAll, setShowAll] = useState(thisIndexScanCount === 0);

  const filteredQueryData = queryData
    .filter(
      makeFilter(searchTerm, "normalizedQuery", "truncatedQuery", "scanLabel"),
    )
    .filter((query) => {
      return showAll || query.newIndex === index.id;
    });
  const noRowsText =
    queryData.length === 0
      ? `No queries found to ${showAll ? "be using" : "prefer"} this index`
      : "No matching queries found";

  const columns: GridColumn<
    (typeof filteredQueryData)[number],
    keyof (typeof filteredQueryData)[number]
  >[] = [
    {
      field: "truncatedQuery",
      header: "Query",
      width: "1fr",
      style: "query",
      renderer: function QueryTextCell({ fieldData, rowData }) {
        const url = databaseQuery(rowData.databaseId, rowData.queryId);
        return (
          <Link title={rowData.normalizedQuery} to={url}>
            {fieldData}
          </Link>
        );
      },
    },
    {
      field: "scanLabel",
      header: "Scan Cost Change",
      width: "15%",
      disableSort: true,
      title: ({ rowData }) => {
        return rowData.scan.combinedExpression;
      },
      renderer: function IndexCell({ rowData }) {
        return (
          <ScanReference scan={rowData.scan}>
            {" "}
            {rowData.estCurrCost != rowData.estNewCost ? (
              <>
                {formatNumber(rowData.estCurrCost)}
                {" → "}
                {formatNumber(rowData.estNewCost)}
              </>
            ) : (
              "no change"
            )}
          </ScanReference>
        );
      },
    },
    {
      field: "serverName",
      header: "Server",
      width: isPrimaryInCluster ? "14%" : false,
    },
    {
      field: "avgTime",
      header: "Avg. Time (ms)",
      style: "number",
      width: "10%",
      defaultSortOrder: "DESC",
      renderer: NumberCell.precision(2),
    },
    {
      field: "callsPerMinute",
      header: "Calls / Min",
      style: "number",
      width: "10%",
      defaultSortOrder: "DESC",
      renderer: NumberCell.precision(3),
    },
    {
      field: "pctOfTotal",
      header: "% All Runtime",
      style: "number",
      width: "12%",
      defaultSortOrder: "DESC",
      renderer: function PcfOfTotalCell({ fieldData }) {
        return formatPercent(fieldData / 100, 2);
      },
    },
  ];

  return (
    <Panel
      title="Affected Queries"
      secondaryTitle={
        <>
          <div>
            <label>
              <input
                type="checkbox"
                className="!mr-1"
                checked={showAll}
                onChange={(evt) => setShowAll(evt.target.checked)}
              />
              Show all queries{" "}
              <Tip
                content={
                  <>
                    If checked, shows all queries that could potentially use
                    this index, even if this index is not the best option in the
                    current index selection recommendation.
                    <br />
                    If unchecked, only shows queries that are expected to use
                    this index (i.e., for which this index offers the lowest
                    cost).
                  </>
                }
              />
            </label>
          </div>
          <SearchInput />
        </>
      }
    >
      <Grid
        noRowsText={noRowsText}
        striped
        defaultSortBy="pctOfTotal"
        sortParam="sq"
        data={filteredQueryData}
        columns={columns}
      />
    </Panel>
  );
};

export default IndexDetails;
